import React, { memo, useMemo, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIsFetching } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Button, Spinner } from '@tcc/crate';
import { getMessage } from 'util/locale';
import { inferUnit } from 'util/unitConversion';
import { formatAvgYield, formatNumberK } from 'util/formatters/numberFormatter';
import { FieldData, FormData, RecommendationOptions } from 'create/bulk/common/types';
import { SEED_DENSITY_RECOMMENDATION_QUERY_KEY } from '../../constants';
import { useReviewPlanRows } from './useReviewPlanRows';
import { RecommendationTableRow } from './types';
import CurveModal from './CurveModal';
import { useAppSelector } from 'app/hooks';
import { getCurrency } from 'user/userSelectors';
import { DeltaValueCell } from '../CustomCells';
import PlanTable from './PlanTable';
import { allowDecimalByCountry } from 'user/selectors';

export interface ReviewPlanProps {
  parentRowData: FieldData & { _id: string; _index: number };
  multipleHybrids?: boolean;
}

export const ReviewPlan: React.FC<ReviewPlanProps> = memo(
  function ReviewPlan({ parentRowData, multipleHybrids }: ReviewPlanProps) {
    const { control, setValue, getValues } = useFormContext<FormData>();
    const [openViewCurveModal, setOpenViewCurveModal] = useState(false);
    const currency = useAppSelector(getCurrency);
    const isDecimalCountry = useAppSelector(allowDecimalByCountry);

    const fieldIndex = useMemo(() => {
      const fields = getValues('fields');
      
      return fields.findIndex((f) => f.hybrid?.id === parentRowData.hybrid?.id && f.id === parentRowData.id);
    }, [getValues, parentRowData.id, parentRowData.hybrid?.id]);

    const field = useWatch({
      control,
      name: `fields.${fieldIndex}`,
    });

    const data = useReviewPlanRows(field);
    const isFetching = useIsFetching({
      queryKey: [SEED_DENSITY_RECOMMENDATION_QUERY_KEY, parentRowData.id]
    });

    const isLoading = useMemo(() => isFetching > 0 || !data.length, [isFetching, data.length]);

    const handleRowSelection = useCallback((id) => {
      setValue(`fields.${fieldIndex}.selectedRecommendation`, id);
    }, [setValue, fieldIndex]);

    const columns: ColumnDef<RecommendationTableRow>[] = useMemo(
      () => [
        {
          id: 'select',
          header: getMessage(
            'bulk-fvss-review-scripts-avg-fixed-population-table-header',
            { unit: inferUnit('seed-density').symbol.quantitySpecific }
          ),
          cell: ({ row }) => {
            return (
              <label className="select-cell">
                <input
                  type="radio"
                  name={`recommendation-${parentRowData._id}`}
                  value={row.original.id}
                  onChange={() => handleRowSelection(row.original.id)}
                  checked={row.original.isRowSelected}
                />
                <span>{formatNumberK(row.original.pop)}</span>
                {row.original.id === RecommendationOptions.FVSS_RECOMMENDATION && (
                  <span className="recommendation-label" data-test-id="recommendation-label">
                    {getMessage('text__recommended')}
                  </span>
                )}
              </label>
            );
          },
        },
        {
          accessorKey: 'yield',
          header: `${getMessage('estimated-yield-short')} (${
            inferUnit('area-density.yield').symbol.quantitySpecific
          })`,
          cell: ({ row }) => (
            <span data-test-id={`yield-cell-${row.id}`}>
              {formatAvgYield(row.original.yield, isDecimalCountry)}
            </span>
          ),
        },
        {
          accessorKey: 'bags',
          header: getMessage('bags-needed'),
        },
        {
          accessorKey: 'seedCost',
          header:
          `${getMessage('price-product')} (${getMessage('per-unit', { unit: inferUnit('area').label.universal })})`,
        },
        {
          id: 'revenue',
          header: `${getMessage('bulk-fvss-review-plan-expected-revenue')} (${
            getMessage('per-unit', { unit: inferUnit('area').label.universal })
          })`,
          cell: ({ row }) => {
            return (
              <div className="revenue-cell" onClick={() => handleRowSelection(row.original.id)}>
                <span>{row.original.revenue}</span>
                {row.original.delta > 0 && (
                  <DeltaValueCell
                    currency={currency}
                    delta={row.original.delta}
                  />
                )}
              </div>
            );
          },
        },
      ],
      [parentRowData._id, handleRowSelection, isDecimalCountry, currency]
    );

    const styledData = useMemo(
      () =>
        data?.map((row) => ({
          ...row,
          isRowSelected: field?.selectedRecommendation === row.id,
        })),
      [data, field?.selectedRecommendation]
    );

    const onClose = useCallback(() => {
      setOpenViewCurveModal(false);
    }, [setOpenViewCurveModal]);

    return (
      <section className="review-plan-section" data-test-id="review-plan-section">
        {isLoading ? (
          <div className="loading-plan-wrapper">
            <Spinner testId="loading-spinner" />
            <p>{getMessage('bulk-loading-subheader-text')}</p>
          </div>
        ) : (
          <>
            {openViewCurveModal && (
              <CurveModal
                field={{...field, _id: parentRowData._id, _index: parentRowData._index }}
                fieldIndex={fieldIndex}
                onClose={onClose}
                multipleHybrids={multipleHybrids}
              />
            )}
            <div className="review-plan-button">
              <Button
                mode="secondary"
                type="button"
                onClick={() => setOpenViewCurveModal(true)}
                testId="view-curve-button"
              >
                {getMessage('bulk-fvss-review-plan-view-curve-button')}
              </Button>
            </div>
            <PlanTable columns={columns} data={styledData} handleRowSelection={handleRowSelection} />
          </>
        )}
      </section>
    );
  },
  (prevProps, nextProps) =>
    prevProps.parentRowData._id === nextProps.parentRowData._id
);
